
import { useContext, useEffect, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphWidget } from '../../../components/GraphWidget/GraphWidget';
import { useSavedGraph } from '../../../components/GraphWidget/useSavedGraph';
import { GraphViewOverlay } from '../../../pages/SocialGraphPOC/GraphViewOverlay';
import { Platforms } from "./Platforms";
import { Countries } from './Countries';
import { Verification } from './Verification';
import { Audience } from './Audience';
import { Compromised } from './Compromised';
import { Bots } from './Bots';
import { StateAffiliated } from './StateAffiliated';
import { TopActorsByPosts } from './TopActorsByPosts';
import {BreakdownsContext} from './contexts/BreakdownsContext';
import { WidgetContentModal } from '../../../components/WidgetContentModal/WidgetContentModal';
import { TopsWidget } from './TopsWidget';
import { ampli } from '../../../ampli';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { AddToNarrativeModal } from '../../../components/AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../../../components/AddToSourceGroupModal/AddToSourceGroupModal';
import { platformMapping } from '../../../utils/platforms';
import { AFFILIATION_TYPE_VALUES } from '../../../utils/affiliationTypes';
import { CountriesBarChart } from './CountriesBarChart';
import { StateAffiliatedChart } from './StateAffiliatedChart';
import { StateAffiliatedCountriesChart } from './StateAffilatedCountriesChart';
import { TopStateAffiliated } from './TopStateAffiliated';
import { HighRiskChart } from './HighRiskChart';
import { TopHighRisk } from './TopHighRisk';
import { TopBots } from './TopBots';
import { BotsChart } from './BotsChart';

import styles from '../NarrativePage.module.scss';
import { af } from 'date-fns/locale';

export const Breakdowns = ({isShare, narrative}) => {
  const { t } = useTranslation();

  const {
    isModalOpen,
    closeModal,
    sourceGroupFilter,
    tagsFilter,
    platformsFilter,
    sourcesTypesFilter,
    countriesFilter,
    countriesExcludeFilter,
    sourcesSubtypesFilter,
    affiliationCountryId,
    affiliationTypesFilter,
    affiliationTypesExcludeFilter,
    audienceFilter,
    sourceFilter,
    widgetName,
    modalTitle,
    fetchStateAffiliated,
    fetchHighRisk,
    fetchInauthenticBehavior,
    affiliationCountriesExcludeFilter,
    highRiskExcludeFilter,
    botsExcludeFilter,
  } = useContext(BreakdownsContext);

  const [currentUser] = useCurrentUser();
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');
  const showGraphWidget = featureFlags?.includes('social_graph');

  // a threat is loaded as a narrative (narrative.is_threat) but its graph is a
  // separate case-graph source type — send threat_id and label the widget "Threat graph"
  const isThreat = Boolean(narrative?.is_threat);
  const graphWidget = useSavedGraph(
    isThreat ? 'threat' : 'case',
    narrative?.id,
    Boolean(showGraphWidget),
  );
  // graph opens in an overlay (not a route) so closing it keeps the user's
  // place on this page — scroll position, tabs, filters
  const [isGraphOverlayOpen, setIsGraphOverlayOpen] = useState(false);
  const [addToCaseModal, setAddToCaseModal] = useState({
    isOpen: false,
    platforms: null,
    sourceGroups: null,
    sources: null,
    countries: null,
    countriesExclude: false,
    showOnlyBots: false,
    discreditations: null,
    affiliationCountries: null,
    affiliationExclude: false,
    affiliationTypes: null,
    affiliationTypesExclude: false,
    highRiskExclude: false,
    botsExcludeFilter: false,
    botsType: null,
    audienceRange: null,
    isVerified: false,
    suspendedByPlatform: false,
    immatureAccounts: false,
  });
  const [addToGroupModal, setAddToGroupModal] = useState({
    isOpen: false,
    platforms: null,
    sourceGroups: null,
    sources: null,
    countries: null,
    countriesExclude: false,
    showOnlyBots: false,
    discreditations: null,
    affiliationCountries: null,
    affiliationExclude: false,
    affiliationTypes: null,
    affiliationTypesExclude: false,
    highRiskExclude: false,
    botsExcludeFilter: false,
    botsType: null,
    audienceRange: null,
    isVerified: false,
    suspendedByPlatform: false,
    immatureAccounts: false,
  });

  useEffect(() => {
    fetchStateAffiliated(narrative.id);
    fetchHighRisk(narrative.id);
    fetchInauthenticBehavior(narrative.id);
    ampli.track({
      event_type: 'Open Actors tab',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        narrative_id: narrative?.id,
      }
    });
  }, []);
  
  return (
    <div className={styles.breakdownsContainer}>
      {/* <div>{t("Click on any name, digit, actor or source name to view it's details. Or simply click the 'View details' button to view details of the widget")}</div> */}

      {isShare || !showGraphWidget ? '' : (
        <div className={styles.breakdownsRow}>
          <GraphWidget
            title={isThreat ? t('Threat graph') : t('Case graph')}
            description={
              isThreat
                ? t('Explore the structure of actor connections in this threat. Find communities, key spreaders, and bridges between them.')
                : t('Explore the structure of actor connections in this case. Find communities, key spreaders, and bridges between them.')
            }
            generatingHint={t("Usually takes up to 20 minutes. You can leave this page, we'll notify you when it's ready.")}
            status={graphWidget.status}
            generatedAt={graphWidget.generatedAt}
            stats={graphWidget.stats}
            isBusy={graphWidget.isBusy}
            showDateRange={false}
            entityName={narrative?.name}
            graphName={graphWidget.graphName}
            graphStartDate={graphWidget.startDate}
            graphEndDate={graphWidget.endDate}
            onGenerate={graphWidget.generate}
            onCancel={graphWidget.cancel}
            onRegenerate={graphWidget.regenerate}
            onOpen={() => graphWidget.graphId && setIsGraphOverlayOpen(true)}
          />
        </div>
      )}

      {isGraphOverlayOpen && graphWidget.graphId && (
        <GraphViewOverlay
          graphId={graphWidget.graphId}
          onClose={() => setIsGraphOverlayOpen(false)}
        />
      )}

      <div className={styles.breakdownsRow}>
        <Platforms narrative={narrative} isShare={isShare} />
        <CountriesBarChart narrative={narrative} isShare={isShare} />
      </div>

      <div className={styles.breakdownsRow}>
        <Audience narrative={narrative} isShare={isShare} />
      </div>

      <div className={styles.breakdownsRow}>
        <TopsWidget narrative={narrative} isShare={isShare} />
      </div>

      <h2>{t('State-affiliated actors')}</h2>

      <div className={styles.breakdownsRow}>
        <StateAffiliatedChart
          narrative={narrative}
          isShare={isShare}
          mode='actors'
          isClickable={true}
        />
        <TopStateAffiliated
          narrative={narrative}
          isShare={isShare}
          details={true}
        />
      </div>

      <div className={styles.breakdownsRow}>
        <StateAffiliatedCountriesChart
          narrative={narrative}
          isShare={isShare}
          mode='actors'
          isClickable={true}
        />
      </div>

      {hideRiskActors ? '' : <h2>{t('High-risk accounts')}</h2>}

      {hideRiskActors ? '' : <div className={styles.breakdownsRow}>
        <HighRiskChart
          narrative={narrative}
          isShare={isShare}
          mode='actors'
          isClickable={true}
        />
        <TopHighRisk
          narrative={narrative}
          isShare={isShare}
          details={true}
        />
      </div>}

      <h2>{t('Inauthentic behavior')}</h2>

      <div className={styles.breakdownsRow}>
        <BotsChart
          narrative={narrative}
          isShare={isShare}
          mode='actors'
          isClickable={true}
        />
        <TopBots
          narrative={narrative}
          isShare={isShare}
          details={true}
        />
      </div>

      {isModalOpen ? (
        <WidgetContentModal
          infoModal={isModalOpen}
          isChart={true}
          title={modalTitle || ''}
          platformOption={platformsFilter ? [platformsFilter] : []}
          type={sourcesTypesFilter}
          subType={sourcesSubtypesFilter}
          stateAffiliatedId={affiliationCountryId}
          stateAffiliatedExclude={affiliationCountriesExcludeFilter}
          affiliationTypes={affiliationTypesFilter}
          affiliationTypesExclude={affiliationTypesExcludeFilter}
          highRiskExclude={highRiskExcludeFilter}
          botsExclude={botsExcludeFilter}
          countryId={countriesFilter}
          // platformsOptions={platformOptions}
          audienceRange={audienceFilter}
          narrative={narrative}
          sourceGroup={sourceGroupFilter}
          tags={tagsFilter}
          source={sourceFilter}
          countryIdExclude={countriesExcludeFilter}
          isShare={isShare}
          showAddToActions={true}
          onChange={(bool) => {
            closeModal();
          }}
          widgetName={widgetName || null}
          onAddToCase={(bool, sourceId) => {
            setAddToCaseModal({
              isOpen: bool,
              platforms: platformsFilter
                ? [platformMapping[platformsFilter?.value]]
                : [],
              sourceGroups: sourceGroupFilter || [],
              sources: sourceId ? [sourceId] : sourceFilter ? sourceFilter : [],
              audienceRange: audienceFilter,
              isVerified: sourcesTypesFilter?.value === 'is_verified',
              suspendedByPlatform: sourcesSubtypesFilter === 'suspended_by_platform',
              immatureAccounts: sourcesSubtypesFilter === 'immature_account',
              countries: countriesFilter?.map((country) => country.value),
              countriesExclude: countriesExcludeFilter,
              showOnlyBots:
                sourcesTypesFilter?.value === 'is_bot' &&
                sourcesTypesFilter?.bool,
              botsType: [
                'immature_account',
                'suspended_by_platform',
                'inauthentic_behavior',
              ].includes(sourcesSubtypesFilter)
                ? sourcesSubtypesFilter
                : null,
              discreditations:
                sourcesTypesFilter?.value === 'discreditated' &&
                !sourcesSubtypesFilter
                  ? [
                      'SANCTIONS',
                      'DISINFORMATION',
                      'HACKED_OR_STOLEN',
                      'INVOLVED_IN_INFLUENCE_OPERATIONS',
                    ]
                  : sourcesTypesFilter?.value === 'discreditated' &&
                    sourcesSubtypesFilter
                  ? [sourcesSubtypesFilter]
                  : [],
              highRiskExclude: highRiskExcludeFilter,
              botsExcludeFilter: botsExcludeFilter,
              affiliationCountries:
                sourcesTypesFilter?.value === 'state_affiliated' &&
                affiliationCountryId
                  ? affiliationCountryId.map(id => {return { value: id }})
                  : [],
              affiliationExclude: affiliationCountriesExcludeFilter,
              affiliationTypes: affiliationTypesExcludeFilter
                ? [...AFFILIATION_TYPE_VALUES]
                : affiliationTypesFilter,
              affiliationTypesExclude: affiliationTypesExcludeFilter,
            });
          }}
          onAddToGroup={(bool, sourceId) => {
            setAddToGroupModal({
              isOpen: bool,
              platforms: platformsFilter
                ? [platformMapping[platformsFilter?.value]]
                : [],
              sourceGroups: sourceGroupFilter ? sourceGroupFilter : [],
              sources: sourceId ? [sourceId] : sourceFilter ? sourceFilter : [],
              countries: countriesFilter?.map((country) => country.value),
              countriesExclude: countriesExcludeFilter,
              audienceRange: audienceFilter,
              isVerified: sourcesTypesFilter?.value === 'is_verified',
              suspendedByPlatform:
                sourcesSubtypesFilter === 'suspended_by_platform',
              immatureAccounts: sourcesSubtypesFilter === 'immature_account',
              showOnlyBots:
                sourcesTypesFilter?.value === 'is_bot' &&
                sourcesTypesFilter?.bool,
              botsType: [
                'immature_account',
                'suspended_by_platform',
                'inauthentic_behavior',
              ].includes(sourcesSubtypesFilter)
                ? sourcesSubtypesFilter
                : null,
              discreditations:
                sourcesTypesFilter?.value === 'discreditated' &&
                !sourcesSubtypesFilter
                  ? [
                      'SANCTIONS',
                      'DISINFORMATION',
                      'HACKED_OR_STOLEN',
                      'INVOLVED_IN_INFLUENCE_OPERATIONS',
                    ]
                  : sourcesTypesFilter?.value === 'discreditated' &&
                    sourcesSubtypesFilter
                  ? [sourcesSubtypesFilter]
                  : [],
              highRiskExclude: highRiskExcludeFilter,
              botsExcludeFilter: botsExcludeFilter,
              affiliationCountries:
                sourcesTypesFilter?.value === 'state_affiliated' &&
                affiliationCountryId
                  ? affiliationCountryId.map(id => {return { value: id }})
                  : [],
              affiliationExclude: affiliationCountriesExcludeFilter,
              affiliationTypes: affiliationTypesExcludeFilter
                ? [...AFFILIATION_TYPE_VALUES]
                : affiliationTypesFilter,
              affiliationTypesExclude: affiliationTypesExcludeFilter,
            });
          }}
        />
      ) : (
        ''
      )}

      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={narrative.id}
          areAllMessagesSelected={true}
          platforms={addToCaseModal.platforms}
          sourceGroups={addToCaseModal.sourceGroups}
          sources={addToCaseModal.sources}
          countries={addToCaseModal.countries}
          countriesExclude={addToCaseModal.countriesExclude}
          showOnlyBots={addToCaseModal.showOnlyBots}
          botsType={addToCaseModal.botsType}
          discreditations={addToCaseModal.discreditations}
          affiliationCountries={addToCaseModal.affiliationCountries}
          affiliationCountriesExclude={addToCaseModal.affiliationExclude}
          affiliationTypes={addToCaseModal.affiliationTypes}
          affiliationTypesExclude={addToCaseModal.affiliationTypesExclude}
          highRiskExclude={addToCaseModal.highRiskExclude}
          botsExclude={addToCaseModal.botsExcludeFilter}
          audienceRange={addToCaseModal.audienceRange}
          isVerified={addToCaseModal.isVerified}
          suspendedByPlatform={addToCaseModal.suspendedByPlatform}
          immatureAccount={addToCaseModal.immatureAccounts}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              platforms: null,
              sourceGroups: null,
              sources: null,
              countries: null,
              showOnlyBots: false,
              discreditations: null,
              audienceRange: null,
              affiliationCountries: null,
              affiliationExclude: false,
              affiliationTypes: null,
              affiliationTypesExclude: false,
              highRiskExclude: false,
              botsExcludeFilter: false,
              botsType: null,
              isVerified: false,
              suspendedByPlatform: false,
              immatureAccounts: false,
            });
          }}
        />
      ) : (
        ''
      )}
    {addToGroupModal.isOpen ? (
      <AddToSourceGroupModal
        isOpen={addToGroupModal.isOpen}
        deduplication={false}
        areAllMessagesSelected={true}
        platforms={addToGroupModal.platforms}
        sourceGroups={addToGroupModal.sourceGroups}
        sourcesFilter={addToGroupModal.sources}
        countries={addToGroupModal.countries}
        countriesExclude={addToGroupModal.countriesExclude}
        showOnlyBots={addToGroupModal.showOnlyBots}
        botsType={addToGroupModal.botsType}
        discreditations={addToGroupModal.discreditations}
        affiliationCountries={addToGroupModal.affiliationCountries}
        affiliationCountriesExclude={addToGroupModal.affiliationExclude}
        affiliationTypes={addToGroupModal.affiliationTypes}
        affiliationTypesExclude={addToGroupModal.affiliationTypesExclude}
        highRiskExclude={addToGroupModal.highRiskExclude}
        botsExclude={addToGroupModal.botsExcludeFilter}
        audienceRange={addToGroupModal.audienceRange}
        isVerified={addToGroupModal.isVerified}
        suspendedByPlatform={addToGroupModal.suspendedByPlatform}
        immatureAccount={addToGroupModal.immatureAccounts}
        narrative={narrative}
        onChange={(bool) => {
          setAddToGroupModal({
            isOpen: bool,
            platforms: null,
            sourceGroups: null,
            sources: null,
            countries: null,
            showOnlyBots: false,
            discreditations: null,
            affiliationCountries: null,
            affiliationExclude: false,
            affiliationTypes: null,
            affiliationTypesExclude: false,
            highRiskExclude: false,
            botsExcludeFilter: false,
            botsType: null,
            audienceRange: null,
            isVerified: false,
            suspendedByPlatform: false,
            immatureAccounts: false,
          });
        }}
      />
    ) : (
      ''
    )}
    </div>
  );
}

